import { createRootRoute, Link, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode, type ReactElement } from 'react';
import { Zap } from 'lucide-react';
import { AuthProvider, useAuth } from '@/lib/auth';
import { AppShell } from '@/components/organisms/app-shell';
import { Button } from '@/components/atoms/button';
import { ErrorState } from '@/components/molecules/error-state';
import { QuickLogDialog } from '@/components/quick-log-dialog';
import { RecorderProvider } from '@/lib/recorder/recorder-context';
import { FloatingRecorderHost } from '@/components/organisms/floating-recorder-host';

const PUBLIC_PATHS: readonly string[] = ['/login', '/dev-takes-fixtures'];

function AuthGuard({ children }: { children: ReactNode }): ReactElement {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !PUBLIC_PATHS.includes(location.pathname)) {
      void navigate({ to: '/login' as string });
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-[var(--ink-3)]">Loading…</p>
      </div>
    );
  }

  return <>{children}</>;
}

function RootComponent(): ReactElement {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const showNav = isAuthenticated && !PUBLIC_PATHS.includes(location.pathname);
  const [isQuickLogOpen, setIsQuickLogOpen] = useState(false);

  return (
    <AuthGuard>
      <div className="min-h-screen bg-[var(--bg-page)] text-[var(--ink)]">
        {showNav ? (
          <AppShell>
            <main>
              <Outlet />
            </main>
          </AppShell>
        ) : (
          <main>
            <Outlet />
          </main>
        )}
        {showNav && (
          <button
            type="button"
            aria-label="Quick log a tune"
            onClick={() => setIsQuickLogOpen(true)}
            className="fixed bottom-20 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--brand)] text-white shadow-lg shadow-[color:var(--brand)]/30 transition-transform hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 lg:bottom-8 lg:right-8"
          >
            <Zap className="h-6 w-6" aria-hidden />
          </button>
        )}
        {isQuickLogOpen && <QuickLogDialog onClose={() => setIsQuickLogOpen(false)} />}
        <FloatingRecorderHost />
      </div>
    </AuthGuard>
  );
}

function NotFound(): ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg-page)] px-4">
      <ErrorState
        title="Page not found"
        description="That route doesn't exist — it may have been renamed or removed."
        action={
          <Link to="/">
            <Button size="lg" fullWidth>
              Back to dashboard
            </Button>
          </Link>
        }
      />
    </div>
  );
}

export const Route = createRootRoute({
  component: () => (
    <AuthProvider>
      <RecorderProvider>
        <RootComponent />
      </RecorderProvider>
    </AuthProvider>
  ),
  notFoundComponent: NotFound,
});
